import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { pacienteService } from '../services';
import PacienteMedicamentos from '../components/PacienteMedicamentos';
import HistoricoMedicamentos from '../components/HistoricoMedicamentos';
import { 
  ArrowLeft, 
  Edit, 
  User, 
  FileText,
  MapPin,
  MessageSquare,
  Calendar,
  AlertTriangle,
  Pill,
  History
} from 'lucide-react';

const DetalhesPaciente = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [paciente, setPaciente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [abaAtiva, setAbaAtiva] = useState('medicamentos');

  const carregarPaciente = useCallback(async () => {
    try {
      setLoading(true);
      const data = await pacienteService.getById(id);
      setPaciente(data);
    } catch (error) {
      console.error('Erro ao carregar paciente:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados do paciente' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    carregarPaciente();
  }, [carregarPaciente]);

  const formatarCpf = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCartaoSus = (cartao) => {
    if (!cartao) return '-';
    return cartao.replace(/(\d{3})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!paciente) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <AlertTriangle size={48} color="#ff6b6b" style={{ marginBottom: '15px' }} />
          <h3>Paciente não encontrado</h3>
          <p style={{ color: '#667', marginBottom: '20px' }}>
            O paciente solicitado não existe ou foi removido.
          </p>
          <button 
            onClick={() => navigate('/pacientes')}
            className="btn btn-primary"
          >
            <ArrowLeft size={20} />
            Voltar para Pacientes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <User size={32} />
            Detalhes do Paciente
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Informações completas do paciente
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link 
            to={`/pacientes/editar/${paciente.id}`}
            className="btn btn-primary"
          >
            <Edit size={20} />
            Editar
          </Link>
          <button 
            onClick={() => navigate('/pacientes')}
            className="btn btn-secondary"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            color: '#333', 
            borderBottom: '2px solid #007bff', 
            paddingBottom: '10px',
            marginBottom: '20px',
            display: 'flex',
            gap: '10px'
          }}>
            <User size={24} />
            {paciente.nome}
          </h2>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px'
        }}>
          {/* Informações Pessoais */}
          <div>
            <h3 style={{ 
              color: '#495057', 
              marginBottom: '20px',
              display: 'flex',
              gap: '8px'
            }}>
              <FileText size={20} />
              Informações Pessoais
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div className="info-item">
                <label>Nome Completo:</label>
                <span>{paciente.nome}</span>
              </div>
              
              <div className="info-item">
                <label>CPF:</label>
                <span style={{ fontFamily: 'monospace' }}>
                  {formatarCpf(paciente.cpf)}
                </span>
              </div>
              
              <div className="info-item">
                <label>RG:</label>
                <span>{paciente.rg || '-'}</span>
              </div>
              
              <div className="info-item">
                <label>Cartão SUS:</label>
                <span style={{ fontFamily: 'monospace' }}>
                  {formatarCartaoSus(paciente.cartaoSus)}
                </span>
              </div>
            </div>
          </div>

          {/* Outras Informações */}
          <div>
            <h3 style={{ 
              color: '#495057', 
              marginBottom: '20px',
              display: 'flex',
              gap: '8px'
            }}>
              <MapPin size={20} />
              Outras Informações
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div className="info-item">
                <label>Endereço:</label>
                <span>{paciente.endereco || '-'}</span>
              </div>
              
              <div className="info-item">
                <label>Data de Cadastro:</label>
                <span style={{ display: 'flex', gap: '5px' }}>
                  <Calendar size={16} />
                  {formatarData(paciente.createdAt)}
                </span>
              </div>
              
              <div className="info-item">
                <label>Última Atualização:</label>
                <span style={{ display: 'flex',  gap: '5px' }}>
                  <Calendar size={16} />
                  {formatarData(paciente.updatedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Observações */}
        {paciente.observacoes && (
          <div style={{ marginTop: '30px' }}>
            <h3 style={{ 
              color: '#495057', 
              marginBottom: '15px',
              display: 'flex',
              gap: '8px'
            }}>
              <MessageSquare size={20} />
              Observações
            </h3>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              whiteSpace: 'pre-wrap'
            }}>
              {paciente.observacoes}
            </div>
          </div>
        )}

        {/* Sistema de Abas */}
        <div style={{ marginTop: '40px' }}>
          {/* Cabeçalho das Abas */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #e9ecef',
            marginBottom: '30px'
          }}>
            <button
              onClick={() => setAbaAtiva('medicamentos')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: abaAtiva === 'medicamentos' ? '3px solid #007bff' : '3px solid transparent',
                color: abaAtiva === 'medicamentos' ? '#007bff' : '#6c757d',
                fontWeight: abaAtiva === 'medicamentos' ? 'bold' : 'normal',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                if (abaAtiva !== 'medicamentos') {
                  e.target.style.color = '#495057';
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseOut={(e) => {
                if (abaAtiva !== 'medicamentos') {
                  e.target.style.color = '#6c757d';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <Pill size={20} />
              Medicamentos Vinculados
            </button>
            
            <button
              onClick={() => setAbaAtiva('historico')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: 'transparent',
                borderBottom: abaAtiva === 'historico' ? '3px solid #007bff' : '3px solid transparent',
                color: abaAtiva === 'historico' ? '#007bff' : '#6c757d',
                fontWeight: abaAtiva === 'historico' ? 'bold' : 'normal',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                if (abaAtiva !== 'historico') {
                  e.target.style.color = '#495057';
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseOut={(e) => {
                if (abaAtiva !== 'historico') {
                  e.target.style.color = '#6c757d';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <History size={20} />
              Histórico de Modificações
            </button>
          </div>

          {/* Conteúdo das Abas */}
          <div>
            {abaAtiva === 'medicamentos' && (
              <PacienteMedicamentos pacienteId={paciente.id} />
            )}
            
            {abaAtiva === 'historico' && (
              <HistoricoMedicamentos pacienteId={paciente.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalhesPaciente;